import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Calendar, 
  Users, 
  Phone, 
  Mail, 
  MapPin, 
  CreditCard, 
  FileText,
  Package,
  Plane,
  Building2,
  Edit,
  Send,
  Download,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface BookingDetailsDialogProps {
  booking: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

export const BookingDetailsDialog = ({ 
  booking, 
  open, 
  onOpenChange, 
  onEdit,
  onDelete 
}: BookingDetailsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [sendingConfirmation, setSendingConfirmation] = useState(false);

  const getServiceTypeIcon = (type: string) => {
    switch (type) {
      case "package": return <Package className="h-5 w-5" />;
      case "flight": return <Plane className="h-5 w-5" />;
      case "hotel": return <Building2 className="h-5 w-5" />;
      default: return <FileText className="h-5 w-5" />;
    }
  };

  const getServiceTypeName = (type: string) => {
    switch (type) {
      case "package": return "باقة سياحية";
      case "flight": return "حجز طيران";
      case "hotel": return "حجز فندق";
      default: return "خدمة";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "confirmed": return "bg-success/10 text-success border-success/20";
      case "pending": return "bg-warning/10 text-warning border-warning/20";
      case "cancelled": return "bg-destructive/10 text-destructive border-destructive/20";
      default: return "bg-muted";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "confirmed": return "مؤكد";
      case "pending": return "في الانتظار";
      case "cancelled": return "ملغي";
      default: return status;
    }
  };

  const handleSendConfirmation = async () => {
    setSendingConfirmation(true);
    try {
      // Create booking confirmation record
      const { error } = await supabase
        .from("booking_confirmations")
        .insert({
          booking_id: booking.id,
          booking_type: booking.type,
          confirmation_type: "email",
          recipient_contact: booking.clientEmail || booking.mobile,
          delivery_method: booking.clientEmail ? "email" : "sms",
          subject: `تأكيد حجز ${getServiceTypeName(booking.type)} - ${booking.bookingNumber}`,
          content: `
            عزيزي/عزيزتي ${booking.clientName},
            
            نؤكد لكم حجز ${getServiceTypeName(booking.type)} كما يلي:
            
            رقم الحجز: ${booking.bookingNumber}
            الخدمة: ${booking.service}
            التاريخ: ${format(new Date(booking.checkIn), "dd MMMM yyyy", { locale: ar })}
            المبلغ الإجمالي: ${booking.totalAmount.toLocaleString()} ${booking.currency}
            
            شكراً لثقتكم بنا.
            
            مع تحيات فريق العمل
          `,
          status: "sent",
          sent_at: new Date().toISOString(),
        });

      if (error) throw error;

      toast.success("تم إرسال تأكيد الحجز بنجاح");
    } catch (error) {
      console.error("Error sending confirmation:", error);
      toast.error("حدث خطأ أثناء إرسال التأكيد");
    } finally {
      setSendingConfirmation(false);
    }
  };

  const handleGenerateInvoice = () => {
    // In a real app, this would generate and download a PDF invoice
    toast.success("سيتم إنشاء الفاتورة قريباً");
  };

  const handleDeleteBooking = async () => {
    if (!confirm("هل أنت متأكد من حذف هذا الحجز؟")) return;
    
    setLoading(true);
    try {
      // In a real app, you'd delete from the appropriate table based on booking type
      toast.success("تم حذف الحجز بنجاح");
      onDelete?.();
      onOpenChange(false);
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("حدث خطأ أثناء حذف الحجز");
    } finally {
      setLoading(false);
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl font-bold">
            {getServiceTypeIcon(booking.type)}
            تفاصيل الحجز - {booking.bookingNumber}
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-6">
            {/* Header Info */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    {getServiceTypeIcon(booking.type)}
                    {getServiceTypeName(booking.type)}
                  </CardTitle>
                  <Badge className={getStatusColor(booking.status)}>
                    {getStatusText(booking.status)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">رقم الحجز:</span>
                    <span>{booking.bookingNumber}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">تاريخ السفر:</span>
                    <span>{format(new Date(booking.checkIn), "dd MMMM yyyy", { locale: ar })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">المبلغ الإجمالي:</span>
                    <span className="text-lg font-bold text-success">
                      {booking.totalAmount.toLocaleString()} {booking.currency}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">الخدمة:</span>
                    <span>{booking.service}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  بيانات العميل
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">الاسم:</span>
                    <span>{booking.clientName}</span>
                  </div>
                  {booking.clientPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">الهاتف:</span>
                      <span>{booking.clientPhone}</span>
                    </div>
                  )}
                  {booking.clientEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">البريد الإلكتروني:</span>
                      <span>{booking.clientEmail}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Booking Details Based on Type */}
            {booking.type === "package" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Package className="h-5 w-5" />
                    تفاصيل الباقة
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">الوجهة:</span>
                      <span>{booking.destination}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد المسافرين:</span>
                      <span>{booking.passengerCount}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد الغرف:</span>
                      <span>{booking.roomCount}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">المدة:</span>
                      <span>
                        {booking.checkOutDate ? 
                          `${Math.ceil((new Date(booking.checkOutDate).getTime() - new Date(booking.checkIn).getTime()) / (1000 * 60 * 60 * 24))} أيام` :
                          "غير محدد"
                        }
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {booking.type === "flight" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Plane className="h-5 w-5" />
                    تفاصيل الطيران
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Plane className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">الرحلة:</span>
                      <span>{booking.service}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد المسافرين:</span>
                      <span>{booking.passengerCount || 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">تاريخ المغادرة:</span>
                      <span>{format(new Date(booking.checkIn), "dd MMMM yyyy", { locale: ar })}</span>
                    </div>
                    {booking.returnDate && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">تاريخ العودة:</span>
                        <span>{format(new Date(booking.returnDate), "dd MMMM yyyy", { locale: ar })}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {booking.type === "hotel" && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building2 className="h-5 w-5" />
                    تفاصيل الفندق
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">الفندق:</span>
                      <span>{booking.service}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد الضيوف:</span>
                      <span>{booking.guestCount || booking.passengerCount || 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد الغرف:</span>
                      <span>{booking.roomCount || 1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">عدد الليالي:</span>
                      <span>
                        {booking.checkOutDate ? 
                          `${Math.ceil((new Date(booking.checkOutDate).getTime() - new Date(booking.checkIn).getTime()) / (1000 * 60 * 60 * 24))} ليلة` :
                          "غير محدد"
                        }
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            {booking.notes && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    ملاحظات
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed">{booking.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </ScrollArea>

        <Separator />

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 justify-between">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onEdit}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              تعديل الحجز
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleSendConfirmation}
              disabled={sendingConfirmation}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {sendingConfirmation ? "جاري الإرسال..." : "إرسال تأكيد"}
            </Button>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleGenerateInvoice}
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              تحميل الفاتورة
            </Button>
          </div>
          
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={handleDeleteBooking}
            disabled={loading}
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {loading ? "جاري الحذف..." : "حذف الحجز"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};